/**
 * Unified Status Badge Utilities
 * Eliminates duplication across ShiftRequestsTab, AvailableShiftsTab, EmployeeShiftsTab
 * Provides consistent status badge rendering for shift requests and general status indicators
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, XCircle, AlertCircle, User } from 'lucide-react';

// Unified status configuration for shift requests
export interface RequestStatusConfig {
  label: string;
  variant: 'default' | 'destructive' | 'outline' | 'secondary';
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
}

// Status configurations for different types
export const SHIFT_REQUEST_STATUS_CONFIG: Record<string, RequestStatusConfig> = {
  pending: {
    label: 'Pending',
    variant: 'secondary',
    icon: Clock,
    description: 'Request is awaiting manager approval'
  },
  approved: {
    label: 'Approved',
    variant: 'default',
    icon: CheckCircle,
    description: 'Request has been approved and shift assigned'
  },
  rejected: {
    label: 'Rejected',
    variant: 'destructive',
    icon: XCircle,
    description: 'Request has been rejected by manager'
  },
  cancelled: {
    label: 'Cancelled',
    variant: 'outline',
    icon: XCircle,
    description: 'Request has been cancelled by employee'
  },
};

// Alternative status labels for different contexts
export const AVAILABLE_SHIFT_STATUS_CONFIG: Record<string, RequestStatusConfig> = {
  pending: {
    label: 'Requested',
    variant: 'secondary',
    icon: Clock,
    description: 'You have requested this shift'
  },
  approved: {
    label: 'Approved',
    variant: 'default',
    icon: CheckCircle,
    description: 'Your request has been approved'
  },
  rejected: {
    label: 'Rejected',
    variant: 'destructive',
    icon: XCircle,
    description: 'Your request was not approved'
  },
};

export interface StatusBadgeProps {
  status: string | null;
  config?: Record<string, RequestStatusConfig>;
  className?: string;
  size?: 'sm' | 'default' | 'lg';
  showIcon?: boolean;
}

/**
 * Unified Status Badge Component
 * Replaces duplicated getStatusBadge functions across components
 */
export const UnifiedStatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  config = SHIFT_REQUEST_STATUS_CONFIG,
  className = '',
  size = 'default',
  showIcon = true,
}) => {
  if (!status) return null;

  const statusConfig = config[status];
  if (!statusConfig) {
    // Fallback for unknown statuses
    return (
      <Badge variant="outline" className={`flex items-center gap-1 ${className}`}>
        <AlertCircle className="w-3 h-3" />
        {status}
      </Badge>
    );
  }

  const { label, variant, icon: Icon } = statusConfig;
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    default: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5'
  };

  return (
    <Badge 
      variant={variant} 
      className={`flex items-center gap-1 ${sizeClasses[size]} ${className}`}
    >
      {showIcon && <Icon className="w-3 h-3" />}
      {label}
    </Badge>
  );
};

/**
 * Convenient wrapper functions for specific contexts
 */

// For shift requests management (manager view)
export const ShiftRequestStatusBadge: React.FC<Omit<StatusBadgeProps, 'config'>> = (props) => (
  <UnifiedStatusBadge {...props} config={SHIFT_REQUEST_STATUS_CONFIG} />
);

// For available shifts (employee view)
export const AvailableShiftStatusBadge: React.FC<Omit<StatusBadgeProps, 'config'>> = (props) => (
  <UnifiedStatusBadge {...props} config={AVAILABLE_SHIFT_STATUS_CONFIG} />
);

/**
 * Helper function to get status configuration for external use
 */
export const getStatusConfig = (
  status: string | null, 
  context: 'requests' | 'available' = 'requests'
): RequestStatusConfig | null => {
  if (!status) return null;
  
  const config = context === 'requests' 
    ? SHIFT_REQUEST_STATUS_CONFIG 
    : AVAILABLE_SHIFT_STATUS_CONFIG;
    
  return config[status] || null;
};

/**
 * Helper function to create status badge (for backward compatibility)
 * Replaces inline getStatusBadge functions
 */
export const createStatusBadge = (
  status: string | null, 
  context: 'requests' | 'available' = 'requests',
  options: Partial<StatusBadgeProps> = {}
) => {
  const config = context === 'requests' 
    ? SHIFT_REQUEST_STATUS_CONFIG 
    : AVAILABLE_SHIFT_STATUS_CONFIG;
    
  return React.createElement(UnifiedStatusBadge, {
    status,
    config,
    ...options
  });
};

// Export configurations for direct use
export { SHIFT_REQUEST_STATUS_CONFIG as RequestStatusConfig };
export { AVAILABLE_SHIFT_STATUS_CONFIG as AvailableStatusConfig };